import type { UiNode, UiNodeInputAttributes } from '@ory/client';
import { useEffect, useState } from 'react';
import { FormItem } from '@/components/FormItem';
import Icon from '@/components/Icon/Icon';
import { emailSchema } from '@/components/Input/Email/email.schema';
import Input from '@/components/Input/Input';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

/** 表示順序: 入力フィールドは中間に配置 */
export const ORDER = 50;
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Email inputノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるemail inputフィールドをレンダリングします。
 * 既存のInputEmailコンポーネントと同じように、リアルタイムバリデーションと多言語対応を実装しています。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-input-nodes
 */
export function EmailInput({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes;

  // フックは常に同じ順序で呼ばれる必要があるため、早期リターンの前に配置
  // Inputはcontrolled componentなので、stateで管理
  const [value, setValue] = useState<string>(attrs.value as string || '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));

  // サーバーからのエラーメッセージが出たら、クライアント側のバリデーションエラーはクリア
  // フックは常に同じ順序で呼ばれる必要があるため、条件付きリターンの前に配置
  useEffect(() => {
    if (errorMessages && errorMessages.length > 0) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
      setValidationError(null);
    }
  }, [errorMessages]);

  // email inputの場合はtype="email"またはname="identifier"である必要がある
  if (attrs.type !== 'email' && attrs.name !== 'identifier' && attrs.name !== 'email') {
    return null;
  }
  const validate = (val: string): string | null => {
    // 未入力の場合はエラーを出さない（requiredはOry側に寄せる）
    if (!val) return null;
    const result = emailSchema.safeParse(val);
    if (!result.success) {
      const issue = result.error.issues[0];
      return issue.message;
    }
    return null;
  };
  const handleChange = (next: string) => {
    setValue(next);
    setValidationError(validate(next));
  };
  const errorMessage = errorMessages?.[0] ?? validationError ?? undefined;
  return <FormItem fieldTitle={label} optional={!attrs.required} errorMessage={errorMessage} className="w-full" fieldInput={<Input type="email" name={attrs.name} value={value} onChange={e => handleChange(e.target.value)} width="100%" prefix={<Icon iconName="Mail" />} placeholder={(attrs as {
    placeholder?: string;
  }).placeholder} disabled={disabled ?? attrs.disabled} required={attrs.required} autoComplete={attrs.autocomplete as string | undefined} error={Boolean(errorMessage)} />} />;
}